import { useState, useEffect } from "react";
import axios from "axios";
import AdminLayout from "../../components/layout/AdminLayout";
import { API } from "../../App";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Skeleton } from "../../components/ui/skeleton";
import { toast } from "sonner";
import { extractApiErrorMessage } from "../../lib/safeData";
import {
  Package,
  ShoppingBag,
  Users,
  DollarSign,
  TrendingUp,
  Clock,
  CheckCircle2,
  Truck,
  Link2,
} from "lucide-react";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        withCredentials: true,
      });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
      const status = error.response?.status;
      if (status === 403) {
        toast.error("Your account does not have admin access");
      } else if (status === 401) {
        toast.error("Session expired — sign in again if stats do not reload");
      } else {
        toast.error(extractApiErrorMessage(error, "Failed to load dashboard stats"));
      }
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: "Total Products",
      value: stats?.total_products || 0,
      icon: Package,
      color: "text-blue-600",
      bg: "bg-blue-100",
    },
    {
      title: "Total Orders",
      value: stats?.total_orders || 0,
      icon: ShoppingBag,
      color: "text-green-600",
      bg: "bg-green-100",
    },
    {
      title: "Total Users",
      value: stats?.total_users || 0,
      icon: Users,
      color: "text-purple-600",
      bg: "bg-purple-100",
    },
    {
      title: "Total Revenue",
      value: `$${(stats?.total_revenue || 0).toFixed(2)}`,
      icon: DollarSign,
      color: "text-orange-600",
      bg: "bg-orange-100",
    },
  ];

  const orderStats = stats?.orders_by_status || {};

  return (
    <AdminLayout>
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-900" data-testid="admin-dashboard-title">
            Dashboard
          </h1>
          <p className="text-slate-600">
            Welcome to the CheepSweepz Concierge Admin Panel
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {loading
            ? [...Array(4)].map((_, i) => (
                <Card key={i}>
                  <CardContent className="p-6">
                    <Skeleton className="h-16 w-full" />
                  </CardContent>
                </Card>
              ))
            : statCards.map((stat) => (
                <Card key={stat.title} className="border-slate-200">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">{stat.title}</p>
                        <p className="text-2xl font-bold text-slate-900">
                          {stat.value}
                        </p>
                      </div>
                      <div className={`p-3 rounded-sm ${stat.bg}`}>
                        <stat.icon className={`h-6 w-6 ${stat.color}`} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
        </div>

        {/* Order Status Breakdown */}
        <div className="grid lg:grid-cols-2 gap-6">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg">Orders by Status</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3, 4].map((i) => (
                    <Skeleton key={i} className="h-10 w-full" />
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  {[
                    { key: "pending_payment", label: "Pending Payment", icon: Clock, color: "text-yellow-600" },
                    { key: "paid", label: "Paid", icon: CheckCircle2, color: "text-green-600" },
                    { key: "processing", label: "Processing", icon: Package, color: "text-blue-600" },
                    { key: "shipped", label: "Shipped", icon: Truck, color: "text-purple-600" },
                    { key: "delivered", label: "Delivered", icon: CheckCircle2, color: "text-emerald-600" },
                  ].map((status) => (
                    <div
                      key={status.key}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-sm"
                    >
                      <div className="flex items-center gap-3">
                        <status.icon className={`h-5 w-5 ${status.color}`} />
                        <span className="font-medium">{status.label}</span>
                      </div>
                      <span className="text-lg font-bold">
                        {orderStats[status.key] || 0}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <a
                  href="/admin/submit-link"
                  className="p-4 border-2 border-orange-200 rounded-sm hover:border-orange-500 hover:bg-orange-50 transition-colors text-center bg-orange-50/50"
                  data-testid="quick-action-submit-link"
                >
                  <Link2 className="h-8 w-8 mx-auto mb-2 text-orange-600" />
                  <span className="text-sm font-medium text-orange-900">Import Product Link</span>
                </a>
                <a
                  href="/admin/products"
                  className="p-4 border border-slate-200 rounded-sm hover:border-orange-500 hover:bg-orange-50 transition-colors text-center"
                  data-testid="quick-action-products"
                >
                  <Package className="h-8 w-8 mx-auto mb-2 text-slate-600" />
                  <span className="text-sm font-medium">Manage Products</span>
                </a>
                <a
                  href="/admin/orders"
                  className="p-4 border border-slate-200 rounded-sm hover:border-orange-500 hover:bg-orange-50 transition-colors text-center"
                  data-testid="quick-action-orders"
                >
                  <ShoppingBag className="h-8 w-8 mx-auto mb-2 text-slate-600" />
                  <span className="text-sm font-medium">View Orders</span>
                </a>
                <a
                  href="/admin/settings"
                  className="p-4 border border-slate-200 rounded-sm hover:border-orange-500 hover:bg-orange-50 transition-colors text-center"
                  data-testid="quick-action-settings"
                >
                  <TrendingUp className="h-8 w-8 mx-auto mb-2 text-slate-600" />
                  <span className="text-sm font-medium">Pricing Settings</span>
                </a>
                <a
                  href="/"
                  className="p-4 border border-slate-200 rounded-sm hover:border-orange-500 hover:bg-orange-50 transition-colors text-center"
                  data-testid="quick-action-store"
                >
                  <DollarSign className="h-8 w-8 mx-auto mb-2 text-slate-600" />
                  <span className="text-sm font-medium">View Store</span>
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
